import { SpaceshipLoader } from "@/components/spaceship-loader/spaceship-loader";
import CardNextEvent from "@/components/card-next-event/card-next-event";
import { useRouteHistoryContext } from "@/contexts/route-history-context/useRouteHistoryContext";
import { useUserContext } from "@/contexts/user/useUserContext";
import { getIsNewUserLocalStorage } from "@/utils/local-storage/user/local-storage";
import { Navigate, useLocation, Location } from "react-router-dom";
import AcceptTermsAndConditionsModal from "@/components/accept-terms-and-conditions-modal/accept-terms-and-conditions-modal";

const ROUTES = {
  LOGIN: '/login',
  REGISTER: '/register',
  HOME: '/home',
} as const;

const styles = {
  contentLoading: {
    height: "100vh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
} as const;

interface ProtectedLayoutProps {
  isPrivate?: boolean;
  children: React.ReactNode;
}

const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({
  children,
  isPrivate = true
}) => {
  const { user, loading, error, loginInBackground } = useUserContext();
  const location = useLocation();
  const { pathname } = location;
  const { pathHistory } = useRouteHistoryContext();
  const { currentPath } = pathHistory;

  const handleRedirectToLastPath = () => (
    <Navigate to={currentPath || ROUTES.HOME} replace />
  );

  const handleRedirectToLogin = (currentLocation: Location) => (
    <Navigate to={ROUTES.LOGIN} state={{ from: currentLocation }} replace />
  );

  const renderLoader = () => (
    <div style={styles.contentLoading}>
      <SpaceshipLoader height="150px" />
    </div>
  );

  const isDisconnected = !loading && !user?.main_key && !loginInBackground;

  if (user?.status === 2) {
    return <CardNextEvent />
  }

  if (pathname === ROUTES.LOGIN) {
    return loginInBackground ? handleRedirectToLastPath() : children;
  }

  if (user?.main_key && getIsNewUserLocalStorage() && pathname !== ROUTES.REGISTER) {
    return <Navigate to={ROUTES.REGISTER} replace />;
  }

  if (isPrivate) {
    if (loading && !loginInBackground) {
      return renderLoader();
    }

    if (isDisconnected || error) {
      return handleRedirectToLogin(location);
    }
  }

  return <>{children}
    {import.meta.env.VITE_ACCEPT_TERMS_MODAL === 'true' &&
      <AcceptTermsAndConditionsModal />
    }
  </>;
};

export default ProtectedLayout;
